/**
 * MomentProvider + useMoment — moment-aware adaptation context.
 *
 * Wraps the app (inside EffectiveEmployeeProvider) and exposes the current
 * OperationalContext to any surface that opts in via `useMoment()`.
 *
 * Data inputs:
 *   - listBlackouts()    → active blackouts overlapping today
 *   - listMyLeaves()     → most recent approved leave (returning detection)
 *   - getDashboard()     → team coverage % (peak detection)
 *   - hasPendingConflict comes from the pending leave list (conflict detection)
 *
 * All queries re-use query keys already in use elsewhere in the shell so the
 * results come from the TanStack Query cache — zero extra network requests
 * when the cache is warm.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { roleAtLeast, roleSchema } from '@lms/shared-types'
import { useSession } from '../auth/auth-context'
import { listBlackouts } from '../api/blackouts'
import { getDashboard } from '../api/dashboard'
import { listMyLeaves } from '../api/me'
import {
  computeMoment,
  readDensity,
  writeDensity,
  type Density,
  type Moment,
  type OperationalContext,
} from './moment'
import type { Blackout, Leave } from '@lms/shared-types'

// ── Context shape ─────────────────────────────────────────────────────────────

export interface MomentContextValue {
  moment: Moment
  density: Density
  reason: string | null
  setDensity: (next: Density) => void
}

const MomentContext = createContext<MomentContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────

interface MomentProviderProps {
  children: ReactNode
}

/** Today's ISO date string — computed once per render cycle, stable. */
function todayIso(): string {
  return new Date().toISOString().split('T')[0]!
}

export function MomentProvider({ children }: MomentProviderProps): JSX.Element {
  const [density, setDensityState] = useState<Density>(readDensity)
  const { user } = useSession()
  // /api/v1/dashboard requires manager-or-above. Skip the prefetch for plain
  // employees so they don't generate 403 noise in DevTools every page load.
  // The teamCoveragePct derive falls back to null in that case (handled below).
  const parsedRole = user ? roleSchema.safeParse(user.role) : null
  const canSeeDashboard =
    parsedRole?.success === true && roleAtLeast(parsedRole.data, 'manager')

  const setDensity = useCallback((next: Density): void => {
    writeDensity(next)
    setDensityState(next)
  }, [])

  // ── Data queries ─────────────────────────────────────────────────────────

  const blackoutsQuery = useQuery({
    queryKey: ['blackouts'],
    queryFn: listBlackouts,
    staleTime: 5 * 60_000,
  })

  const myLeavesQuery = useQuery({
    queryKey: ['shell', 'my-leaves'],
    queryFn: () => listMyLeaves(),
    staleTime: 60_000,
  })

  const dashboardQuery = useQuery({
    queryKey: ['shell', 'dashboard-stats'],
    queryFn: () => getDashboard({ horizonDays: 30 }),
    staleTime: 60_000,
    enabled: canSeeDashboard,
  })

  // ── Derive inputs from query data ────────────────────────────────────────

  const today = todayIso()

  const activeBlackouts = useMemo(
    () => filterActiveBlackouts(blackoutsQuery.data ?? [], today),
    [blackoutsQuery.data, today],
  )

  const mostRecentApprovedLeave = useMemo(
    () => findMostRecentApproved(myLeavesQuery.data ?? [], today),
    [myLeavesQuery.data, today],
  )

  const hasPendingConflict = useMemo(
    () => detectPendingConflict(myLeavesQuery.data ?? []),
    [myLeavesQuery.data],
  )

  const weekCoveragePct = useMemo(
    () => deriveCoveragePct(dashboardQuery.data?.teams ?? null),
    [dashboardQuery.data],
  )

  // ── Compute moment ────────────────────────────────────────────────────────

  const ctx = useMemo((): OperationalContext => {
    return computeMoment({
      activeBlackouts,
      mostRecentApprovedLeave,
      hasPendingConflict,
      weekCoveragePct,
    })
  }, [activeBlackouts, mostRecentApprovedLeave, hasPendingConflict, weekCoveragePct])

  const value = useMemo(
    (): MomentContextValue => ({
      moment: ctx.moment,
      density,
      reason: ctx.reason,
      setDensity,
    }),
    [ctx.moment, ctx.reason, density, setDensity],
  )

  return <MomentContext.Provider value={value}>{children}</MomentContext.Provider>
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns the current operational context for this user.
 *
 * Must be called within a `<MomentProvider>`. Returns a stable default
 * (steady / comfortable) when called outside the provider so isolated
 * component tests don't need the full provider tree.
 */
export function useMoment(): MomentContextValue {
  const ctx = useContext(MomentContext)
  if (ctx === null) {
    // Graceful fallback outside provider — surfaces still render correctly;
    // the chip simply won't appear since moment === 'steady'.
    return {
      moment: 'steady',
      density: 'comfortable',
      reason: null,
      setDensity: () => undefined,
    }
  }
  return ctx
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

function filterActiveBlackouts(
  blackouts: ReadonlyArray<Blackout>,
  today: string,
): Array<{ name: string; startDate: string; endDate: string }> {
  return blackouts
    .filter((b) => b.startDate <= today && b.endDate >= today)
    .map((b) => ({ name: b.name, startDate: b.startDate, endDate: b.endDate }))
}

function findMostRecentApproved(
  leaves: ReadonlyArray<Leave>,
  today: string,
): { endDate: string } | null {
  const recent = leaves
    .filter((l) => l.status === 'approved' && l.endDate < today)
    .sort((a, b) => b.endDate.localeCompare(a.endDate))

  return recent[0] ? { endDate: recent[0].endDate } : null
}

function detectPendingConflict(leaves: ReadonlyArray<Leave>): boolean {
  // A conflict is detected when the employee has a pending leave with an
  // escalation target set — this signals the system detected a coverage issue.
  return leaves.some(
    (l) => (l.status === 'pending' || l.status === 'escalated') && l.escalationTarget !== null,
  )
}

function deriveCoveragePct(
  teams: ReadonlyArray<{ memberCount: number; onLeaveToday: number }> | null,
): number | null {
  if (teams === null) return null
  if (teams.length === 0) return null

  const totalMembers = teams.reduce((sum, t) => sum + t.memberCount, 0)
  const totalOut = teams.reduce((sum, t) => sum + t.onLeaveToday, 0)

  if (totalMembers === 0) return null
  const presentCount = totalMembers - totalOut
  return (presentCount / totalMembers) * 100
}
